import React, { useState, useCallback, useEffect } from 'react'
import { generatePath, Link, useParams } from 'react-router-dom'

import DeliveryZoneForm from '@components/deliveryZone/DeliveryZoneForm'
import SingleZoneMap from '@components/deliveryZone/map/SingleZoneMap'
import LogisticTopMenu from '@components/logistic/LogisticTopMenu'
import DeliveryZonesSubMenu from '@components/logistic/DeliveryZonesSubMenu'

import { Button, ButtonGroup, Panel } from 'rsuite'
import { ArrowBackOutline, ExpandOutline, ContractOutline } from 'react-ionicons'
import { ROUTE_DELIVERY_ZONES_LIST } from '@@partner:config/routes'
import { GeoPoint } from '@@partner:types:app'
import { useFetchSingleDeliveryZone } from '@@partner:graphql/query/deliveryZone/singleDeliveryZone.gql'

export default function DeliveryZoneEditPage () {
  const { zoneId, brandSlug } = useParams<{ zoneId: string; brandSlug: string }>()
  const [ editedZone, setEditedZone ] = useState<GeoPoint[]>([])
  const [ shopId, setShopId ] = useState<number | null>(null)
  const [ isMapExpanded, setIsMapExpanded ] = useState(false)

  const { data, loading } = useFetchSingleDeliveryZone({
    id: zoneId || null
  })

  // Загрузка геоданных зоны при получении данных
  useEffect(() => {
    if (!loading && data?.singleDeliveryZone) {
      const zone = data.singleDeliveryZone
      if (zone.zoneGeoDeliveryZone && zone.zoneGeoDeliveryZone.length > 0) {
        setEditedZone(zone.zoneGeoDeliveryZone)
      }
      if (zone.shop_id) {
        setShopId(zone.shop_id)
      }
    }
  }, [ data?.singleDeliveryZone, loading ])

  const handleZoneChanged = useCallback((zone: GeoPoint[]) => {
    setEditedZone(zone)
  }, [])

  const toggleMapExpand = useCallback(() => {
    setIsMapExpanded((prev) => !prev)
  }, [])

  // Полноэкранный режим карты
  if (isMapExpanded) {
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          backgroundColor: '#fff'
        }}
      >
        <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 1001 }}>
          <Button 
            appearance="primary" 
            onClick={toggleMapExpand}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <ContractOutline color="#fff" height="18px" width="18px" />
            Свернуть карту
          </Button>
        </div>
        <SingleZoneMap
          zoneId={zoneId || null}
          zoneGeoPoints={editedZone}
          onZoneChanged={handleZoneChanged}
          shopId={shopId}
          fullscreen
        />
      </div>
    )
  }

  return (
    <div className="DeliveryZoneEditPage">
      <h1 className="u-heading">
        {zoneId ? `Редактирование зоны доставки #${zoneId}` : 'Создание зоны доставки'}
      </h1>
      <LogisticTopMenu />
      <DeliveryZonesSubMenu />
      <div className="my-3">
        <ButtonGroup>
          <Button
            appearance="ghost"
            as={Link}
            to={generatePath(ROUTE_DELIVERY_ZONES_LIST, { brandSlug })}
          >
            <ArrowBackOutline />
            Назад к списку
          </Button>
        </ButtonGroup>
      </div>

      {/* Карта сверху на всю ширину */}
      <Panel 
        header={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Карта зоны доставки</span>
            <Button 
              size="sm" 
              appearance="subtle" 
              onClick={toggleMapExpand}
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <ExpandOutline height="16px" width="16px" />
              Развернуть
            </Button>
          </div>
        } 
        shaded 
        className="mb-4"
      >
        <SingleZoneMap
          zoneId={zoneId || null}
          zoneGeoPoints={editedZone}
          onZoneChanged={handleZoneChanged}
          shopId={shopId}
        />
        <div className="mt-2 text-muted" style={{ fontSize: '12px' }}>
          Нажмите на карту, чтобы добавить точки полигона зоны. 
          Перетаскивайте точки для редактирования.
        </div>
      </Panel>

      {/* Форма на всю ширину */}
      <DeliveryZoneForm zoneId={zoneId || null} newZone={editedZone} />
    </div>
  )
}
